import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { MetaBadge } from '../../components/MetaIcon';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useNavigate } from 'react-router-dom';
import { dashboardTokens } from '../../../Dashboard/Components/dashboardCardStyles';
import { GoogleIcon } from '../../components/GoogleIcon';
import { platformMeta } from '../../components/leadsMeta';
import { IntegrationAccountStatus } from '../../../../services/marketingLeads';

const relativeTime = (iso: string | null): string => {
  if (!iso) return 'never synced';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'never synced';

  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return 'synced just now';
  if (minutes < 60) return `last synced ${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `last synced ${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  return `last synced ${days} day${days === 1 ? '' : 's'} ago`;
};

const STATUS_CHIP: Record<string, { label: string; color: string; bg: string }> = {
  CONNECTED: { label: 'Connected', color: '#22c55e', bg: '#22c55e22' },
  DISCONNECTED: { label: 'Not connected', color: '#94a3b8', bg: '#64748b22' },
  // Only a dead token reaches ERROR now (see adCampaignSync.js) — so this
  // really does mean "reconnect", unlike the old catch-all.
  ERROR: { label: 'Reconnect required', color: '#ef4444', bg: '#ef444422' },
};

// Meta's Ads Manager wants the numeric id without the act_ prefix; Google Ads
// takes the customer id as-is.
const managerUrl = (platform: 'META' | 'GOOGLE', accountId: string | null) => {
  if (platform === 'GOOGLE') {
    return accountId
      ? `https://ads.google.com/aw/campaigns?__e=${accountId}`
      : 'https://ads.google.com/aw/campaigns';
  }
  return accountId
    ? `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${accountId.replace(/^act_/, '')}`
    : 'https://adsmanager.facebook.com/adsmanager/manage/campaigns';
};

interface ConnectionCardProps {
  platform: 'META' | 'GOOGLE';
  integration: IntegrationAccountStatus | null;
}

export const ConnectionCard: React.FC<ConnectionCardProps> = ({ platform, integration }) => {
  const navigate = useNavigate();
  const meta = platformMeta[platform];
  const status = integration?.status || 'DISCONNECTED';
  const chip = STATUS_CHIP[status] || STATUS_CHIP.DISCONNECTED;
  const accountId = integration?.externalAccountId || null;

  // Meta setup state, straight off the status response. This used to read a
  // field called `leadgenSubscribedAt` that the backend never sent, so the
  // "no Page is subscribed" warning showed for EVERY connected Meta account
  // and told people to reconnect — which wouldn't have helped, because the
  // old connect flow never subscribed a Page at all. The real fields are
  // `pageId` / `pageSubscribedAt` / `setupComplete`, and the fix is the
  // setup dialog on the Leads page, not a reconnect.
  const pages = integration?.pages || [];
  const isMeta = platform === 'META';
  const connected = status === 'CONNECTED';
  const setupIncomplete = isMeta && connected && integration?.setupComplete === false;
  const unsubscribedPages = pages.filter((p) => !p.subscribedAt);
  const pageNotSubscribed = isMeta && connected && !setupIncomplete && unsubscribedPages.length > 0;
  const tokenDays = integration?.tokenExpiresInDays;
  const tokenExpiringSoon =
    isMeta && connected && integration?.tokenType === 'USER' && tokenDays != null && tokenDays <= 14;

  // Deep-link into the Meta setup dialog on the Leads page (it opens itself
  // on ?integration=setup, the same signal the OAuth callback uses).
  const openSetup = () => navigate('/leads?integration=setup');

  const syncErrorText = (() => {
    const err = integration?.lastSyncError;
    if (!err) return null;
    switch (integration?.lastErrorCode) {
      case 'CONFIG':
        return `Server misconfigured (not a Meta problem): ${err}. Fix .env on the API server and restart, then Sync now.`;
      case 'RATE_LIMIT':
        return `Meta rate-limited the last sync: ${err}. It retries tonight.`;
      case 'PERMISSION':
        return `Permission problem: ${err}. Check the connected login's access to the ad account and Page.`;
      case 'TOKEN_INVALID':
        return `Token expired or revoked: ${err}. Reconnect.`;
      default:
        return err;
    }
  })();

  return (
    <Box
      sx={{
        bgcolor: dashboardTokens.cardBg,
        border: `1px solid ${dashboardTokens.border}`,
        borderRadius: dashboardTokens.cardRadius,
        p: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        {platform === 'META' ? (
          <MetaBadge size={48} radius={12} />
        ) : (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              bgcolor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <GoogleIcon size={26} />
          </Box>
        )}

        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
              {meta.label}
            </Typography>
            <Chip
              label={chip.label}
              size="small"
              sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 700, height: 22 }}
            />
            {pages.map((p) => (
              <Chip
                key={p.id}
                label={p.name}
                size="small"
                sx={{
                  bgcolor: p.subscribedAt ? '#64748b22' : '#f59e0b22',
                  color: p.subscribedAt ? dashboardTokens.textSecondary : '#f59e0b',
                  fontWeight: 600,
                  height: 22,
                }}
              />
            ))}
          </Box>
          <Typography sx={{ fontSize: 13, color: dashboardTokens.textSecondary, mt: 0.25 }}>
            Lead generation campaigns · {relativeTime(integration?.lastSyncedAt || null)}
            {isMeta && integration?.externalAccountName && ` · ${integration.externalAccountName}`}
            {isMeta && integration?.externalAccountCurrency && ` (${integration.externalAccountCurrency})`}
          </Typography>
          {syncErrorText && (
            <Typography sx={{ fontSize: 12, color: integration?.lastErrorCode === 'TOKEN_INVALID' ? '#ef4444' : '#f59e0b', mt: 0.5 }}>
              {syncErrorText}
            </Typography>
          )}
          {setupIncomplete && (
            <Typography sx={{ fontSize: 12, color: '#f59e0b', mt: 0.5 }}>
              Connected, but no ad account / Page chosen yet — nothing syncs and no leads arrive until setup is finished.
            </Typography>
          )}
          {pageNotSubscribed && (
            <Typography sx={{ fontSize: 12, color: '#f59e0b', mt: 0.5 }}>
              {unsubscribedPages.map((p) => p.name).join(', ')} {unsubscribedPages.length === 1 ? 'is' : 'are'} chosen but not
              subscribed to lead events — leads from {unsubscribedPages.length === 1 ? 'it' : 'them'} will not arrive. Open setup and
              press "Save & subscribe".
            </Typography>
          )}
          {tokenExpiringSoon && (
            <Typography sx={{ fontSize: 12, color: '#f59e0b', mt: 0.5 }}>
              Login token expires in {tokenDays} day{tokenDays === 1 ? '' : 's'} — reconnect, or switch to a System User token so it never expires.
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {isMeta && (
        <Button
          variant={setupIncomplete || pageNotSubscribed ? 'contained' : 'outlined'}
          size="small"
          disableElevation
          startIcon={<SettingsOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={openSetup}
          sx={
            setupIncomplete || pageNotSubscribed
              ? { textTransform: 'none', fontWeight: 700, px: 2, bgcolor: '#f59e0b', color: '#111', '&:hover': { bgcolor: '#d97706' } }
              : { borderColor: dashboardTokens.border, color: '#e2e8f0', textTransform: 'none', fontWeight: 600, px: 2, '&:hover': { borderColor: '#3d3d3d', bgcolor: '#232323' } }
          }
        >
          {connected ? (setupIncomplete ? 'Finish setup' : 'Manage connection') : 'Connect'}
        </Button>
      )}
      <Button
        variant="outlined"
        size="small"
        endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
        href={managerUrl(platform, accountId)}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          borderColor: dashboardTokens.border,
          color: '#e2e8f0',
          textTransform: 'none',
          fontWeight: 600,
          px: 2,
          '&:hover': { borderColor: '#3d3d3d', bgcolor: '#232323' },
        }}
      >
        View in {meta.label}
      </Button>
      </Box>
    </Box>
  );
};
